import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import API from 'core/Api';
import { setAuthTokensFromResponse } from 'utils/auth';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { useModal } from 'store/modal';
import { usePermissionsContext } from 'store/permissions';
import { useTranslation } from 'react-i18next';
import { useTwoFactorContext } from 'store/two-factor';

interface IInputs {
  code: string;
}

const CodeValidationForm: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const methods = useForm<IInputs>();
  const { setPermissions } = usePermissionsContext();
  const { setIs2FaComplete } = useTwoFactorContext();
  const { showModal } = useModal();
  const [failedAttempts, setFailedAttempts] = useState(0);

  const handleLinkClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    showModal({
      type: 'info',
      heading: t('twoFA.instuctions.heading'),
      size: 'lg',
      content: (
        <div className="d-flex flex-column text-left px-3">
          <p>{t('twoFA.instuctions.step1')}</p>
          <p>{t('twoFA.instuctions.step2')}</p>
          <p>{t('twoFA.instuctions.step3')}</p>
          <p>{t('twoFA.instuctions.step4')}</p>
          <p>{t('twoFA.instuctions.step5')}</p>
          <p>{t('twoFA.instuctions.step6')}</p>
        </div>
      ),
    });
  };

  const onSubmit = (data: IInputs) => {
    return API.post('auth/2fa-validate', {
      code: data.code,
    })
      .then(async res => {
        await setAuthTokensFromResponse(res);

        const permissions = await API.get('permissions');

        setPermissions(permissions.data);

        setIs2FaComplete(true);

        showModal({
          type: 'success',
          content: t('twoFA.activationSuccessMsg'),
        });
        return history.push('/home');
      })
      .catch(err => {
        setFailedAttempts(prevAttempts => prevAttempts + 1);
        setServerErrors(createErrorFromResponse(err).errors, methods.setError);
      });
  };
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      btnLabel={t('twoFA.activate')}
      hideCancelBtn
      isHorizontal
    >
      <FormGroup
        controlId="formGroupCode"
        label={t('twoFA.code')}
        hint={t('twoFA.codeHint')}
        type="tel"
        placeholder="******"
        name="code"
        autoComplete="off"
        isRequired
        isVerificationCode
        isHorizontal
      />
      {failedAttempts >= 3 && (
        <div
          style={{
            color: 'var(--red)',
            fontSize: '80%',
            fontWeight: 300,
            marginLeft: '140px',
            marginTop: '-10px',
            cursor: 'pointer',
          }}
          onMouseOver={e => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseOut={e => {
            e.currentTarget.style.textDecoration = 'none';
          }}
          onFocus={e => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onBlur={e => {
            e.currentTarget.style.textDecoration = 'none';
          }}
          onClick={handleLinkClick}
        >
          {t('twoFA.warning')} {t('twoFA.instuctions.title')}
        </div>
      )}
    </Form>
  );
};

export default CodeValidationForm;
